'use client';

import { useMemo, useState } from 'react';

import { useQuery } from '@apollo/client/react';
import { ArrowBackIosNew, ArrowForwardIos } from '@mui/icons-material';
import { Box, IconButton, Paper, Stack, Typography } from '@mui/material';
import { EventItem } from '@scorenco/components';
import { RouteLink, Types } from '@scorenco/core';

import {
  CompetitionQueryDocument,
  EventsByRoundIdDocument,
} from './_internal/Widget.query.generated';

interface CompetitionWidgetProps {
  competition_id?: number;
}

export const CompetitionWidget = ({
  competition_id,
}: CompetitionWidgetProps) => {
  const competitionQuery = useQuery(CompetitionQueryDocument, {
    variables: { id: competition_id! },
    skip: !competition_id,
    context: {
      role: 'anonymous',
    },
  });
  const competition =
    competitionQuery.data?.competitions_competition_detail_by_id?.[0];

  const nbRounds = competition?.rounds?.length || 0;

  const defaultRound = useMemo(() => {
    if (!competition) return 0;
    const idx = competition?.rounds?.findIndex(
      ({ id }) => id === competition?.competition?.current_round?.[0]?.id
    );
    return idx != null && idx >= 0 ? idx : 0;
  }, [competition]);

  const [userSelectedRound, setUserSelectedRound] = useState<number | null>(
    null
  );
  const selectedRound = userSelectedRound ?? defaultRound;
  const setSelectedRound = setUserSelectedRound;

  const eventsDetails = useQuery(EventsByRoundIdDocument, {
    variables: {
      roundId: competition?.rounds?.[selectedRound]?.id as number,
    },
    skip: !competition?.rounds?.[selectedRound]?.id || !competition_id,
    context: {
      role: 'anonymous',
    },
  });

  if (!nbRounds) {
    return null;
  }

  return (
    <Box sx={{ width: '100%' }}>
      <Paper variant="bordered" sx={{ paddingY: '1rem' }}>
        <Typography variant="h4" sx={{ mb: 2 }}>
          {competition?.name}
        </Typography>
      </Paper>
      <Box sx={{ width: '100%', padding: '2rem' }}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{ width: '100%', mb: 2 }}
        >
          <IconButton
            color="primary"
            onClick={() =>
              selectedRound !== 0
                ? setSelectedRound(selectedRound - 1)
                : setSelectedRound(nbRounds - 1)
            }
          >
            <ArrowBackIosNew />
          </IconButton>
          <Typography variant="h4" sx={{ textTransform: 'uppercase' }}>
            {competition?.rounds?.[selectedRound]?.name}
          </Typography>
          <IconButton
            color="primary"
            onClick={() =>
              selectedRound !== nbRounds - 1
                ? setSelectedRound(selectedRound + 1)
                : setSelectedRound(0)
            }
          >
            <ArrowForwardIos />
          </IconButton>
        </Stack>
        <Stack direction="column" spacing={2}>
          {eventsDetails.data?.competitions_event_detail_by_round_day_id?.map(
            (event, index) => (
              <RouteLink
                key={event.id}
                href={`${process.env.NEXT_PUBLIC_WEB_URL}${eventsDetails.data?.competitions_event_detail_by_round_day_id?.[index]?.url}`}
              >
                <EventItem
                  key={event.id || index}
                  component="a"
                  sx={{
                    textDecoration: 'none',
                    color: 'inherit',
                  }}
                  event={event as Types.Competitions_Event_Detail}
                  showCompetition={false}
                  showDate={true}
                />
              </RouteLink>
            )
          )}
        </Stack>
      </Box>
    </Box>
  );
};
